import { useCallback, useMemo, useRef } from 'react';

import { useStoreContext } from 'features/store';
import { useGetStoreAvailabilityFn } from 'features/store/hooks';
import { isMobileOrderingAvailable } from 'features/store/utils';

import { IRestaurant } from '@rbi-ctg/store';
import { useConfigValue } from 'hooks/configs/use-config-value';
import {
  isAvailableAndNotDisabled,
  useServiceModeStatusGenerator,
} from 'hooks/use-service-mode-status';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { ServiceMode, useServiceModeContext } from 'state/service-mode';
import { PICKUP_SERVICE_MODES } from 'utils/service-mode';

export const useAutoSelectStoreUtils = () => {
  const isManualRestaurantSelectionRef = useRef<boolean>();
  const enableOrdering = useFlag(LaunchDarklyFlag.ENABLE_ORDERING);

  const checkAvailability = useGetStoreAvailabilityFn();
  const restaurantsConfig = useConfigValue({ key: 'restaurants', defaultValue: {} });
  const { generateServiceModeStatusForStore } = useServiceModeStatusGenerator();

  const validMobileOrderingEnvs = useMemo(() => restaurantsConfig.validMobileOrderingEnvs ?? [], [
    restaurantsConfig.validMobileOrderingEnvs,
  ]);

  const { isRestaurantAvailable, setStore, resetStore } = useStoreContext();
  const { setServiceMode } = useServiceModeContext();

  const restaurantCanBeSelected = useCallback(
    (store: IRestaurant) => {
      if (!enableOrdering) {
        return false;
      }

      const serviceModeStatus = generateServiceModeStatusForStore(store);
      const availablePickupServiceModes = PICKUP_SERVICE_MODES.filter(value =>
        // @ts-ignore
        isAvailableAndNotDisabled(serviceModeStatus[value])
      );

      // Store selection 1.0: store can only be selected if there is an available service mode
      return (
        checkAvailability(store) &&
        isMobileOrderingAvailable(store, validMobileOrderingEnvs) &&
        Boolean(availablePickupServiceModes.length)
      );
    },
    [enableOrdering, generateServiceModeStatusForStore, checkAvailability, validMobileOrderingEnvs]
  );

  const getFirstAvailableStore = useCallback(
    async (stores: IRestaurant[]) => {
      for (const store of stores) {
        const isAvailable = await isRestaurantAvailable(store);
        const canBeSelected = restaurantCanBeSelected(store);
        if (isAvailable && canBeSelected) {
          return store;
        }
      }

      return undefined;
    },
    [isRestaurantAvailable, restaurantCanBeSelected]
  );

  const handleConfirmRestaurant = ({
    confirmed,
    restaurantToConfirm,
    onSelect,
  }: {
    confirmed: boolean;
    restaurantToConfirm: IRestaurant | null;
    onSelect: (selectedRestaurant: IRestaurant | null) => void;
  }) => {
    if (restaurantToConfirm && confirmed) {
      setStore(({ ...restaurantToConfirm, hasSelection: true } as unknown) as IRestaurant);
      setServiceMode(ServiceMode.TAKEOUT);
      logEvent(CustomEventNames.STORE_AUTO_SELECTED, EventTypes.Other);
    } else {
      isManualRestaurantSelectionRef.current = true;
      resetStore();
    }
    onSelect(null);
  };

  return {
    getFirstAvailableStore,
    handleConfirmRestaurant,
    isManualRestaurantSelectionRef,
  };
};
